import { useContext, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
} from "@bfc-software/app-toolbox";
import { EmptyState } from "@bfc-software/pallet";
import { ArrowLeft, MapPin } from "react-feather";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { BrandedScreen } from "../../../../-components/branded-screen";

import styles from "./warehouse-front-door.module.css";

interface WarehouseFrontDoorProps {
  /** The product's own name, as it appears in the copy. */
  product: string;
  /**
   * Where to land once a warehouse is settled. A route path carrying `$lang`,
   * `$org` and `$location`, which is the whole contract - this component fills
   * those three and nothing else, so a product's first screen is its own choice.
   */
  to: string;
}

/**
 * The step a warehouse-scoped product takes before any of its screens.
 *
 * Warehouse scope belongs to the products that need one rather than to signing
 * in, so each of them has a door like this: one warehouse is taken without
 * asking, several are offered, none is an explanation rather than an empty list.
 *
 * Choosing re-mints the session against that warehouse, and the redirect waits
 * for it - so the screens below always load with a warehouse-bound session.
 * From then on the URL owns the warehouse and the session follows it, which is
 * the product layout's job rather than this one's.
 *
 * Shared by Engine Room and Outbound. Two copies of this drifted apart the
 * moment one of them fixed something, and every part of it is about the
 * warehouse rather than about either product.
 */
export function WarehouseFrontDoor({ product, to }: WarehouseFrontDoorProps) {
  const params = useParams({ strict: false });
  const lang = params.lang ?? "";
  const org = params.org ?? "";
  const navigate = useNavigate();
  const { state, selectLocation } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.portal,
  });

  // eslint-disable-next-line no-restricted-properties -- org-level: needs an org-only tenancy hook
  const orgId = AuthUtils.getOrgIdByStub(state, org);
  const warehouses = useMemo(
    () => (orgId ? AuthUtils.getLocations(state, orgId) : []),
    [state, orgId],
  );
  const only = warehouses.length === 1 ? warehouses[0] : undefined;

  const sessionWarehouse =
    state.status === AUTHENTICATED ? state.locationId : null;

  // One warehouse is not a choice - take it.
  useEffect(() => {
    if (only && sessionWarehouse !== only.warehouseId) {
      selectLocation(only.warehouseId);
    }
  }, [only, sessionWarehouse, selectLocation]);

  // Wait for the re-minted session before entering, so the screens below never
  // load against the wrong warehouse.
  useEffect(() => {
    if (!sessionWarehouse) return;
    if (!warehouses.some((w) => w.warehouseId === sessionWarehouse)) return;

    navigate({
      to,
      params: { lang, org, location: sessionWarehouse },
      replace: true,
    });
  }, [sessionWarehouse, warehouses, navigate, to, lang, org]);

  if (warehouses.length === 0) {
    return (
      <BrandedScreen>
        <div className={styles.empty}>
          <EmptyState heading={t("warehouse.none.heading")}>
            {t("warehouse.none.body", { product })}
          </EmptyState>
        </div>
        <BackToProducts label={t("warehouse.back")} lang={lang} org={org} />
      </BrandedScreen>
    );
  }

  if (only) {
    return (
      <BrandedScreen>
        <p className={styles.preparing}>
          {t("warehouse.preparing", { name: only.locationName })}
        </p>
      </BrandedScreen>
    );
  }

  return (
    <BrandedScreen>
      <h1 className={styles.heading}>{t("warehouse.heading")}</h1>
      <p className={styles.subheading}>
        {t("warehouse.subheading", { product })}
      </p>

      <ul className={styles.warehouses}>
        {warehouses.map((warehouse) => (
          <li key={warehouse.warehouseId}>
            <button
              type="button"
              className={styles.warehouse}
              onClick={() => selectLocation(warehouse.warehouseId)}
            >
              <MapPin size={17} strokeWidth={1.9} aria-hidden="true" />
              {warehouse.locationName}
            </button>
          </li>
        ))}
      </ul>

      <BackToProducts label={t("warehouse.back")} lang={lang} org={org} />
    </BrandedScreen>
  );
}

function BackToProducts({
  label,
  lang,
  org,
}: {
  label: string;
  lang: string;
  org: string;
}) {
  const navigate = useNavigate();

  return (
    <div className={styles.actions}>
      <button
        type="button"
        className={styles.pill}
        onClick={() =>
          navigate({
            to: "/$lang/orgs/$org",
            params: { lang, org },
          })
        }
      >
        <ArrowLeft size={15} strokeWidth={2} aria-hidden="true" />
        {label}
      </button>
    </div>
  );
}
